import React, { useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import {
  Card,
  Spinner,
  ErrorBox,
  SimpleTable,
} from "../../components/ui/basics";
import CrudModal from "../../components/normative/CrudModal";
import {
  TIPO,
  TIPOLOGIA_STANDARD,
  UNITA_MISURA,
} from "../../constants/normativeTipo";

const TIPOLOGIA_LABEL = Object.fromEntries(
  TIPOLOGIA_STANDARD.map((t) => [t.value, t.label]),
);

export default function RegioniSection() {
  const { apiRequest } = useAuth();
  const {
    data: regioni,
    loading,
    error,
    reload,
  } = useApi("/normative/regioni");
  const [modal, setModal] = useState(null);

  const fields = [
    {
      key: "codice",
      label: "Codice",
      required: true,
      disabled: modal?.mode === "edit",
    },
    { key: "name", label: "Nome", required: true },
    {
      key: "tipologiastandard",
      label: "Tipologia standard",
      type: "select",
      required: true,
      options: TIPOLOGIA_STANDARD,
    },
    {
      key: "unitamisura",
      label: "Unità di misura default",
      type: "select",
      options: UNITA_MISURA,
    },
    { key: "normativa", label: "Riferimento normativo (es. DGR ...)" },
    { key: "descrizione", label: "Descrizione" },
    {
      key: "notturnosoglialetti",
      label: "Copertura notturna — soglia posti letto (vuoto = nessun vincolo)",
      type: "number",
      placeholder: "es. 80",
    },
    {
      key: "notturnofasciaminima",
      label: "Copertura notturna — fascia minima (codice profilo)",
      placeholder: "es. PI_MAL",
    },
    {
      key: "notturnomansione",
      label: "Copertura notturna — mansione richiesta H24",
      placeholder: "es. INF",
    },
    {
      key: "notturnonote",
      label: "Copertura notturna — riferimento normativo",
    },
  ];

  const submit = async (values) => {
    const payload = {
      ...values,
      tipo: TIPO.REGIONE,
      notturnosoglialetti:
        values.notturnosoglialetti === "" || values.notturnosoglialetti == null
          ? null
          : Number(values.notturnosoglialetti),
    };
    if (modal.mode === "create") {
      await apiRequest("/normative/anagrafiche", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    } else {
      await apiRequest(`/normative/anagrafiche/${modal.record.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    }
    reload();
  };

  const elimina = async (r) => {
    if (
      !window.confirm(
        `Eliminare la regione "${r.name}"? Vengono eliminati anche profili e requisiti associati.`,
      )
    )
      return;
    await apiRequest(`/normative/anagrafiche/${r.id}`, { method: "DELETE" });
    reload();
  };

  return (
    <Card
      title="Regioni & Normative"
      actions={
        <button
          onClick={() => setModal({ mode: "create" })}
          className="text-sm bg-primary-600 text-white px-3 py-1.5 rounded hover:bg-primary-700"
        >
          + Nuova regione
        </button>
      }
    >
      {loading && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {regioni && (
        <SimpleTable
          rowKey={(r) => r.id}
          columns={[
            { key: "codice", label: "Codice" },
            { key: "name", label: "Nome" },
            {
              key: "tipologiastandard",
              label: "Tipologia",
              render: (r) =>
                TIPOLOGIA_LABEL[r.tipologiastandard] || r.tipologiastandard,
            },
            { key: "unitamisura", label: "Unità default" },
            { key: "normativa", label: "Riferimento" },
            {
              key: "azioni",
              label: "",
              render: (r) => (
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setModal({ mode: "edit", record: r })}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    Modifica
                  </button>
                  <button
                    onClick={() => elimina(r)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Elimina
                  </button>
                </div>
              ),
            },
          ]}
          rows={regioni}
        />
      )}
      {modal && (
        <CrudModal
          title={
            modal.mode === "create"
              ? "Nuova regione"
              : `Modifica ${modal.record.name}`
          }
          fields={fields}
          initialValues={modal.record || {}}
          onSubmit={submit}
          onClose={() => setModal(null)}
        />
      )}
    </Card>
  );
}
